import { X } from 'lucide-react'
import type React from 'react'
import { useEffect, useState } from 'react'
import styled from 'styled-components'
import { theme } from '../../theme/theme'

const ModalOverlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  opacity: ${(props) => (props.$isOpen ? 1 : 0)};
  visibility: ${(props) => (props.$isOpen ? 'visible' : 'hidden')};
  transition: ${theme.transitions.default};
`

const ModalContainer = styled.div<{ $isOpen: boolean }>`
  background: ${({ theme }) => theme.colors.cardBackground};
  border-radius: ${theme.borderRadius.lg};
  max-width: 600px;
  width: 90vw;
  max-height: 90vh;
  overflow-y: auto;
  transform: ${(props) => (props.$isOpen ? 'scale(1)' : 'scale(0.9)')};
  transition: ${theme.transitions.default};
  box-shadow: ${({ theme }) => theme.shadows.lg};
`

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${theme.spacing.xl};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};

  h2 {
    margin: 0;
    color: ${({ theme }) => theme.colors.text};
    font-size: ${theme.typography.xl};
    font-weight: 600;
  }
`

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  padding: ${theme.spacing.xs};
  border-radius: ${theme.borderRadius.sm};
  transition: ${theme.transitions.default};

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceHover};
    color: ${({ theme }) => theme.colors.text};
  }
`

const ModalBody = styled.div`
  padding: ${theme.spacing.xl};
`

const FormSection = styled.div`
  margin-bottom: ${theme.spacing.xl};

  &:last-child {
    margin-bottom: 0;
  }
`

const FormRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${theme.spacing.lg};
  margin-bottom: ${theme.spacing.lg};
`

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;

  label {
    color: ${({ theme }) => theme.colors.text};
    font-weight: 500;
    margin-bottom: ${theme.spacing.xs};
    font-size: ${theme.typography.sm};
  }
`

const Input = styled.input`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${theme.borderRadius.sm};
  padding: ${theme.spacing.md};
  color: ${({ theme }) => theme.colors.text};
  font-size: ${theme.typography.md};
  transition: ${theme.transitions.default};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.primary}20;
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.textMuted};
  }
`

const Select = styled.select`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${theme.borderRadius.sm};
  padding: ${theme.spacing.md};
  color: ${({ theme }) => theme.colors.text};
  font-size: ${theme.typography.md};
  transition: ${theme.transitions.default};
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.primary}20;
  }
`

const SplitGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${theme.spacing.md};
`

const CheckboxGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${theme.spacing.md};
`

const CheckboxItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};

  input[type="checkbox"] {
    width: 18px;
    height: 18px;
    accent-color: ${({ theme }) => theme.colors.primary};
    cursor: pointer;
  }

  label {
    color: ${({ theme }) => theme.colors.text};
    font-size: ${theme.typography.sm};
    cursor: pointer;
    margin: 0;
  }
`

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${theme.spacing.md};
  padding: ${theme.spacing.xl};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`

const Button = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  background: ${({ theme, $variant }) => ($variant === 'primary' ? theme.colors.surface : theme.colors.surface)};
  color: ${({ theme, $variant }) => ($variant === 'primary' ? theme.colors.textSecondary : theme.colors.text)};
  border: 1px solid ${({ theme, $variant }) => ($variant === 'primary' ? theme.colors.secondary : theme.colors.border)};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  font-size: ${theme.typography.md};
  font-weight: 500;
  cursor: pointer;
  transition: ${theme.transitions.default};

  &:hover:not(:disabled) {
    background: ${({ theme, $variant }) => ($variant === 'primary' ? theme.colors.surfaceHover : theme.colors.surfaceHover)};
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
  }
`

export interface GenerateWorkoutConstraints {
  startDate: string // date in ISO format
  endDate: string // date in ISO format
  trainingPhase: 'base' | 'build' | 'peak' | 'taper' | 'recovery' | 'maintenance'
  weekInPlan: number
  weeksToGoal: number
  lastWeekTSS: number
  plannedWeekTSS: number
  plannedWeekHours: number
  maxHardDays: number
  focus: 'balanced' | 'swimming' | 'cycling' | 'running' | 'strength'
  swimSplit?: number
  bikeSplit?: number
  runSplit?: number
  includeStrength: boolean
  includeMobility: boolean
}

interface GenerateWorkoutConstraintsModalProps {
  initialData: GenerateWorkoutConstraints
  isOpen: boolean
  onClose: () => void
  onSubmit: (constraints: GenerateWorkoutConstraints) => void
  isGenerating?: boolean
}

export const GenerateWorkoutConstraintsModal: React.FC<GenerateWorkoutConstraintsModalProps> = ({
  initialData,
  isOpen,
  onClose,
  onSubmit,
  isGenerating = false,
}) => {
  const [constraints, setConstraints] = useState<GenerateWorkoutConstraints>({
    ...initialData,
  })

  // Close modal on escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown)
      return () => document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, onClose])

  const handleInputChange = (field: keyof GenerateWorkoutConstraints, value: any) => {
    setConstraints((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSubmit(constraints)
  }

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  return (
    <ModalOverlay $isOpen={isOpen} onClick={handleOverlayClick}>
      <ModalContainer $isOpen={isOpen}>
        <ModalHeader>
          <h2>
            Plan Workouts {constraints.startDate.substring(0, 10)} - {constraints.endDate.substring(0, 10)}
          </h2>
          <CloseButton onClick={onClose}>
            <X size={20} />
          </CloseButton>
        </ModalHeader>

        <form onSubmit={handleSubmit}>
          <ModalBody>
            <FormSection>
              <FormRow>
                <FormGroup>
                  <label htmlFor="trainingPhase">Training Phase</label>
                  <Select
                    id="trainingPhase"
                    value={constraints.trainingPhase}
                    onChange={(e) => handleInputChange('trainingPhase', e.target.value)}
                  >
                    <option value="base">Base</option>
                    <option value="build">Build</option>
                    <option value="peak">Peak</option>
                    <option value="taper">Taper</option>
                    <option value="recovery">Recovery</option>
                    <option value="maintenance">Maintenance</option>
                  </Select>
                </FormGroup>

                <FormGroup>
                  <label htmlFor="weekInPlan">Current Week in Plan</label>
                  <Input
                    type="number"
                    id="weekInPlan"
                    min="1"
                    max="52"
                    value={constraints.weekInPlan}
                    onChange={(e) => handleInputChange('weekInPlan', parseInt(e.target.value) || 1)}
                  />
                </FormGroup>

                <FormGroup>
                  <label htmlFor="weeksToGoal">Weeks to Goal</label>
                  <Input
                    type="number"
                    id="weeksToGoal"
                    min="1"
                    max="52"
                    value={constraints.weeksToGoal}
                    onChange={(e) => handleInputChange('weeksToGoal', parseInt(e.target.value) || 1)}
                  />
                </FormGroup>
              </FormRow>

              <FormRow>
                <FormGroup>
                  <label htmlFor="plannedWeekTSS">Target Hours</label>
                  <Input
                    type="number"
                    id="plannedWeekHours"
                    min="0"
                    max="168"
                    step="0.5"
                    placeholder=""
                    value={constraints.plannedWeekHours}
                    onChange={(e) => handleInputChange('plannedWeekHours', parseInt(e.target.value) || 0)}
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <label htmlFor="plannedWeekTSS">Target TSS</label>
                  <Input
                    type="number"
                    id="plannedWeekTSS"
                    min="50"
                    max="10000"
                    placeholder="TSS"
                    value={constraints.plannedWeekTSS}
                    onChange={(e) => handleInputChange('plannedWeekTSS', parseInt(e.target.value) || 0)}
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <label htmlFor="maxHardDays">Max Hard Days</label>
                  <Input
                    type="number"
                    id="maxHardDays"
                    min="0"
                    max="7"
                    value={constraints.maxHardDays}
                    onChange={(e) => handleInputChange('maxHardDays', parseInt(e.target.value) || 0)}
                  />
                </FormGroup>

                <FormGroup>
                  <label htmlFor="lastWeekTSS">Last Week TSS</label>
                  <Input
                    type="number"
                    id="lastWeekTSS"
                    min="0"
                    placeholder="300"
                    value={constraints.lastWeekTSS}
                    onChange={(e) => handleInputChange('lastWeekTSS', parseInt(e.target.value) || 0)}
                  />
                </FormGroup>
              </FormRow>

              <FormRow>
                <FormGroup>
                  <label htmlFor="focus">Focus Discipline</label>
                  <Select
                    id="focus"
                    value={constraints.focus}
                    onChange={(e) => handleInputChange('focus', e.target.value)}
                  >
                    <option value="">Balanced</option>
                    <option value="swimming">Swimming</option>
                    <option value="cycling">Cycling</option>
                    <option value="running">Running</option>
                    <option value="strength">Strength</option>
                  </Select>
                </FormGroup>
              </FormRow>

              <FormGroup>
                <label>Discipline Split (optional - should sum to ~1.0)</label>
                <SplitGrid>
                  <div>
                    <label htmlFor="swimSplit">Swimming</label>
                    <Input
                      type="number"
                      id="swimSplit"
                      min="0"
                      max="1"
                      step="0.1"
                      placeholder="0.2"
                      value={constraints.swimSplit || ''}
                      onChange={(e) =>
                        handleInputChange('swimSplit', e.target.value ? parseFloat(e.target.value) : undefined)
                      }
                    />
                  </div>
                  <div>
                    <label htmlFor="bikeSplit">Cycling</label>
                    <Input
                      type="number"
                      id="bikeSplit"
                      min="0"
                      max="1"
                      step="0.1"
                      placeholder="0.4"
                      value={constraints.bikeSplit || ''}
                      onChange={(e) =>
                        handleInputChange('bikeSplit', e.target.value ? parseFloat(e.target.value) : undefined)
                      }
                    />
                  </div>
                  <div>
                    <label htmlFor="runSplit">Running</label>
                    <Input
                      type="number"
                      id="runSplit"
                      min="0"
                      max="1"
                      step="0.1"
                      placeholder="0.4"
                      value={constraints.runSplit || ''}
                      onChange={(e) =>
                        handleInputChange('runSplit', e.target.value ? parseFloat(e.target.value) : undefined)
                      }
                    />
                  </div>
                </SplitGrid>
              </FormGroup>

              <FormGroup>
                <label>Additional Workouts</label>
                <CheckboxGrid>
                  <CheckboxItem>
                    <input
                      type="checkbox"
                      id="includeStrength"
                      checked={constraints.includeStrength}
                      onChange={(e) => handleInputChange('includeStrength', e.target.checked)}
                    />
                    <label htmlFor="includeStrength">Strength Training</label>
                  </CheckboxItem>
                  <CheckboxItem>
                    <input
                      type="checkbox"
                      id="includeMobility"
                      checked={constraints.includeMobility}
                      onChange={(e) => handleInputChange('includeMobility', e.target.checked)}
                    />
                    <label htmlFor="includeMobility">Mobility/Recovery</label>
                  </CheckboxItem>
                </CheckboxGrid>
              </FormGroup>
            </FormSection>
          </ModalBody>

          <ModalFooter>
            <Button type="button" onClick={onClose} disabled={isGenerating}>
              Cancel
            </Button>
            <Button type="submit" $variant="primary" disabled={isGenerating}>
              {isGenerating ? 'Generating...' : 'Generate Plan'}
            </Button>
          </ModalFooter>
        </form>
      </ModalContainer>
    </ModalOverlay>
  )
}
